import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { PackageOpen, Lock, ArrowLeft, Plus, Trash2, Wand2 } from 'lucide-react';
import nurseryService from '../../services/nursery';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { errorMessage } from '../../utils/apiResponse';
import { rowLabel } from '../../utils/nurseryRowSlots';
import type { DespatchPlace, DespatchPlacePayload, NurseryLocation, RowSide } from '../../types/nursery';
import { PickModeSetting } from '../../components/nursery/despatch/PickModeSetting';

/**
 * /nursery/despatchable — the places whose ready trees orders are allocated
 * from by themselves (2026-10-07; replaced despatch areas and refills).
 *
 * A place is a whole farm, a block, a row (one side or both) or a single
 * location. When an order is confirmed, and when someone presses Allocate,
 * the server fills each line from despatchable ready lots, oldest ready
 * first. Places add up; a place already covered by a broader one is refused.
 */

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

/** What a place is, in words: "Block A · Row 5 left", or the location's own name. */
function placeLabel(p: DespatchPlace, t: TFunction<'nursery', 'DespatchPlaces'>): string {
  if (p.location_name) return [p.block_name, p.location_name].filter(Boolean).join(' · ');
  if (p.block_name == null) return t('wholeFarm');
  if (p.row_number == null) return t('wholeBlockOf', { block: p.block_name });
  const row = rowLabel(p.row_number, p.row_side);
  return p.row_side ? `${p.block_name} · ${row}` : t('rowBothSides', { block: p.block_name, row });
}

const farmOf = (l: NurseryLocation) => l.place_farm_id ?? l.farm_id ?? null;

function DespatchPlaces() {
  const navigate = useNavigate();
  const { t } = useTranslation('nursery', { keyPrefix: 'DespatchPlaces' });
  const { hasAccess: canManage, loading: licenseLoading } = useModuleLicense('nursery', ['manager', 'admin']);
  const [places, setPlaces] = useState<DespatchPlace[] | null>(null);
  const [locations, setLocations] = useState<NurseryLocation[]>([]);
  const [farms, setFarms] = useState<{ id: number; name: string }[]>([]);
  const [farmId, setFarmId] = useState<number | ''>('');
  // '' whole farm, 'b:<id>' a block, 'l:<id>' a place with no block.
  const [where, setWhere] = useState('');
  // '' whole block, 'r:<n>' a row, 'l:<id>' a place in the block with no row.
  const [inBlock, setInBlock] = useState('');
  const [side, setSide] = useState<'' | RowSide>('');
  // '' the whole row (or side), else a position's location id.
  const [position, setPosition] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, locs, pick] = await Promise.all([
        nurseryService.getDespatchPlaces(),
        nurseryService.getLocations({ active: true }),
        nurseryService.getPickers(),
      ]);
      if (p.success) setPlaces(p.data);
      else setError(errorMessage(p, t('loadFailed')));
      if (locs.success) setLocations(locs.data || []);
      if (pick.success) setFarms(pick.data.farms);
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    }
  }, [t]);

  useEffect(() => { if (canManage) load(); }, [canManage, load]);

  const resetBelow = (level: 'farm' | 'where' | 'block' | 'side') => {
    if (level === 'farm') setWhere('');
    if (level === 'farm' || level === 'where') setInBlock('');
    if (level !== 'side') setSide('');
    setPosition('');
  };

  const atFarm = useMemo(() => locations.filter((l) => farmOf(l) === farmId), [locations, farmId]);
  const blocks = useMemo(() => {
    const m = new Map<number, string>();
    for (const l of atFarm) if (l.block_id) m.set(l.block_id, l.block_name || t('blockN', { id: l.block_id }));
    return [...m].sort((a, b) => a[1].localeCompare(b[1], undefined, { numeric: true }));
  }, [atFarm, t]);
  const blockId = where.startsWith('b:') ? Number(where.slice(2)) : null;
  const inThisBlock = useMemo(() => atFarm.filter((l) => l.block_id === blockId), [atFarm, blockId]);
  const rows = useMemo(
    () => [...new Set(inThisBlock.filter((l) => l.row_number != null).map((l) => l.row_number as number))].sort((a, b) => a - b),
    [inThisBlock],
  );
  const rowNumber = inBlock.startsWith('r:') ? Number(inBlock.slice(2)) : null;
  const inRow = inThisBlock.filter((l) => l.row_number === rowNumber);
  const rowSides = [...new Set(inRow.map((l) => l.row_side).filter(Boolean))] as RowSide[];
  const positions = inRow
    .filter((l) => (side ? l.row_side === side : true))
    .sort((a, b) => (a.row_side ?? '').localeCompare(b.row_side ?? '') || (a.sequence_number ?? 0) - (b.sequence_number ?? 0));

  const payload = (): DespatchPlacePayload | null => {
    if (!farmId) return null;
    if (where.startsWith('l:')) return { farm_id: farmId, location_id: Number(where.slice(2)) };
    if (!blockId) return { farm_id: farmId };
    if (inBlock.startsWith('l:')) return { farm_id: farmId, location_id: Number(inBlock.slice(2)) };
    if (rowNumber == null) return { farm_id: farmId, block_id: blockId };
    if (position) return { farm_id: farmId, location_id: position };
    return { farm_id: farmId, block_id: blockId, row_number: rowNumber, row_side: side || null };
  };

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e) {
      setError(errorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const add = () => run(async () => {
    const body = payload();
    if (!body) return;
    const res = await nurseryService.addDespatchPlace(body);
    if (!res.success) throw new Error(errorMessage(res, t('addFailed')));
    setPlaces(res.data);
    resetBelow('farm');
  }, t('addFailed'));

  const remove = (p: DespatchPlace) => {
    if (!window.confirm(t('confirmRemove', { farm: p.farm_name, place: placeLabel(p, t) }))) return;
    run(async () => {
      const res = await nurseryService.removeDespatchPlace(p.id);
      if (!res.success) throw new Error(errorMessage(res, t('removeFailed')));
      setPlaces(res.data);
    }, t('removeFailed'));
  };

  const allocateAll = () => run(async () => {
    const res = await nurseryService.autoAllocate();
    if (!res.success) throw new Error(errorMessage(res, t('allocateFailed')));
    const r = res.data;
    const filled = r.orders.filter((o) => o.allocated.length > 0).length;
    const short = r.orders.filter((o) => o.short.length > 0).length;
    setNotice(r.no_places
      ? t('noPlacesYet')
      : r.trees
        ? `${t('allocated', { count: filled, trees: r.trees })}${short ? ` ${t('stillShort', { n: short })}` : ''}`
        : r.orders.length
          ? t('noneFree', { count: r.orders.length })
          : t('noneWaiting'));
    await load();
  }, t('allocateFailed'));

  if (licenseLoading) return <div className="max-w-3xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div>;
  if (!canManage) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">{t('licenceTitle')}</h2>
          <p className="text-sm text-neutral-500 mb-4">{t('licenceBody')}</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/nursery')}><ArrowLeft className="h-4 w-4 mr-1" /> {t('backToNursery')}</Button>
        </div>
      </div>
    );
  }

  const byFarm = new Map<string, DespatchPlace[]>();
  for (const p of places ?? []) byFarm.set(p.farm_name, [...(byFarm.get(p.farm_name) ?? []), p]);
  const unblocked = atFarm.filter((l) => !l.block_id);
  const blockPlaces = inThisBlock.filter((l) => l.row_number == null);

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<PackageOpen className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="outline" size="sm" disabled={busy || !places?.length} onClick={allocateAll}>
            <Wand2 className="h-4 w-4" /> {t('allocateWaiting')}
          </Button>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {notice && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900 break-words">
            {notice} <Link to="/nursery/despatch" className="underline">{t('despatchPlanner')}</Link>
          </div>
        )}

        <p className="text-sm text-neutral-600">
          <Trans t={t} i18nKey="intro" components={{ strong: <span className="font-medium" /> }} />
        </p>

        <PickModeSetting />

        <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
          <h2 className="text-sm font-semibold text-neutral-800">{t('makeDespatchable')}</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <label htmlFor="dp-farm" className="block text-xs text-neutral-500 mb-1">{t('farm')}</label>
              <select id="dp-farm" value={farmId} className={inputCls}
                onChange={(e) => { setFarmId(e.target.value ? Number(e.target.value) : ''); resetBelow('farm'); }}>
                <option value="">{t('chooseFarm')}</option>
                {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>
            {farmId !== '' && (
              <div className="min-w-0">
                <label htmlFor="dp-where" className="block text-xs text-neutral-500 mb-1">{t('whereOnFarm')}</label>
                <select id="dp-where" value={where} className={inputCls} onChange={(e) => { setWhere(e.target.value); resetBelow('where'); }}>
                  <option value="">{t('wholeFarm')}</option>
                  {blocks.map(([id, name]) => <option key={id} value={`b:${id}`}>{name}</option>)}
                  {unblocked.map((l) => <option key={l.id} value={`l:${l.id}`}>{l.name}</option>)}
                </select>
              </div>
            )}
            {blockId != null && (
              <div className="min-w-0">
                <label htmlFor="dp-block" className="block text-xs text-neutral-500 mb-1">{t('inBlock')}</label>
                <select id="dp-block" value={inBlock} className={inputCls} onChange={(e) => { setInBlock(e.target.value); resetBelow('block'); }}>
                  <option value="">{t('wholeBlock')}</option>
                  {rows.map((r) => <option key={r} value={`r:${r}`}>{rowLabel(r)}</option>)}
                  {blockPlaces.map((l) => <option key={l.id} value={`l:${l.id}`}>{l.name}</option>)}
                </select>
              </div>
            )}
            {rowNumber != null && rowSides.length > 0 && (
              <div className="min-w-0">
                <label htmlFor="dp-side" className="block text-xs text-neutral-500 mb-1">{t('side')}</label>
                <select id="dp-side" value={side} className={inputCls} onChange={(e) => { setSide(e.target.value as '' | RowSide); resetBelow('side'); }}>
                  <option value="">{t('bothSides')}</option>
                  {rowSides.map((s) => <option key={s} value={s}>{s === 'left' ? t('left') : t('right')}</option>)}
                </select>
              </div>
            )}
            {rowNumber != null && positions.length > 0 && (
              <div className="min-w-0">
                <label htmlFor="dp-position" className="block text-xs text-neutral-500 mb-1">{t('position')}</label>
                <select id="dp-position" value={position} className={inputCls} onChange={(e) => setPosition(e.target.value ? Number(e.target.value) : '')}>
                  <option value="">{side === 'left' ? t('allOfRowLeft', { row: rowNumber }) : side === 'right' ? t('allOfRowRight', { row: rowNumber }) : t('allOfRow', { row: rowNumber })}</option>
                  {positions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
            )}
          </div>
          <div className="flex justify-end">
            <Button size="sm" disabled={busy || !farmId} onClick={add}><Plus className="h-4 w-4" /> {t('makeDespatchableButton')}</Button>
          </div>
        </div>

        {places === null ? (
          <div className="h-24 bg-neutral-100 rounded-xl animate-pulse" />
        ) : places.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-6 text-center text-sm text-neutral-500">
            {t('emptyList')}
          </div>
        ) : (
          [...byFarm].map(([farm, list]) => (
            <section key={farm} className="bg-white border border-neutral-200 rounded-xl">
              <h2 className="px-4 py-3 border-b border-neutral-100 text-sm font-semibold text-neutral-800 break-words">{farm}</h2>
              <ul className="divide-y divide-neutral-100">
                {list.map((p) => (
                  <li key={p.id} className="flex items-start justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-neutral-800 break-words">{placeLabel(p, t)}</p>
                      <p className="text-xs text-neutral-500">
                        {t('readyFree', { count: p.ready_free })} · {t('lots', { count: p.lots })}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" disabled={busy} onClick={() => remove(p)} aria-label={t('removeNamed', { farm: p.farm_name, place: placeLabel(p, t) })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </>
  );
}

export default DespatchPlaces;
